import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Space, Typography, message } from 'antd'
import PageLoader from '../../../components/Layouts/PageLoader'
import {
  carRatesUrl,
  dutyBankingRatesUrl,
  formatUrl,
  getRequest,
  postRequest
} from '../../../config'
import ConfirmChanges from './ConfirmChanges'
import RatesHistory from './RatesHistory'
import RatesTable from './RatesTable'
import {
  TABLES,
  changedFields,
  fieldLabels,
  lastChanged,
  localErrors,
  serverErrors,
  toPayload
} from './utils'

const URLS = { banking: dutyBankingRatesUrl, car: carRatesUrl }

// One page for either rate table (spec: 0041_exchange_rates_dashboard). Saving sends only what
// changed; the API adds a new row, so every save shows up in History and can be restored.
const RatesPage = ({ table }) => {
  const config = TABLES[table]
  const url = URLS[table]
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [rates, setRates] = useState(null)
  const [history, setHistory] = useState([])
  const [meta, setMeta] = useState({})
  const [editing, setEditing] = useState(false)
  const [edits, setEdits] = useState({})
  const [errors, setErrors] = useState({})
  const [confirming, setConfirming] = useState([])
  const [restoring, setRestoring] = useState(null)

  const getCurrent = async () => {
    const response = await getRequest(`${url}/current/`, config.title.toLowerCase(), setLoading)
    if (response.success) setRates(response.data)
  }

  const getHistory = async (pageUrl = null) => {
    const response = await getRequest(
      pageUrl ? formatUrl(pageUrl) : `${url}/`,
      'rate history',
      () => {}
    )
    if (response.success) {
      setHistory(response.data.results || [])
      setMeta({ ...response.data })
    }
  }

  const reload = () => {
    getCurrent()
    getHistory()
  }

  useEffect(() => {
    reload()
    // eslint-disable-next-line
  }, [table])

  const startEditing = () => {
    setEdits({})
    setErrors({})
    setEditing(true)
  }

  const stopEditing = () => {
    setEditing(false)
    setEdits({})
    setErrors({})
    setConfirming([])
  }

  const onEdit = (field, value) => {
    setEdits(prev => ({ ...prev, [field]: value }))
    setErrors(prev => {
      const next = { ...prev }
      delete next[field]
      return next
    })
  }

  const save = async (confirm = false) => {
    const found = localErrors(table, rates, edits)
    if (Object.keys(found).length) {
      setErrors(found)
      return
    }
    const changes = changedFields(rates, edits)
    if (!changes.length) {
      message.info('Nothing changed.')
      stopEditing()
      return
    }
    const response = await postRequest(
      `${url}/`,
      { rates: toPayload(changes), confirm },
      config.title.toLowerCase(),
      setSaving
    )
    if (!response.success) {
      setErrors(serverErrors(response.error))
      const detail = response.error?.response?.data?.detail
      if (detail) message.error(detail)
      return
    }
    if (response.data.saved === false) {
      setConfirming(response.data.needsConfirm || [])
      return
    }
    message.success(`${config.title} saved.`)
    stopEditing()
    setRates(response.data.rates)
    getHistory()
  }

  const restore = async row => {
    setRestoring(row.id)
    const response = await postRequest(`${url}/${row.id}/restore/`, {}, 'restore', () => {})
    setRestoring(null)
    if (!response.success) {
      const detail = response.error?.response?.data?.detail
      if (detail) message.error(detail)
      return
    }
    message.success('Rates restored.')
    stopEditing()
    setRates(response.data.rates)
    getHistory()
  }

  return (
    <PageLoader className="container" loading={loading}>
      <div className="card mb-5rem">
        <div className="card-body">
          <Space
            align="start"
            style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}
          >
            <div>
              <h3 style={{ marginBottom: 4 }}>{config.title}</h3>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 4 }}>
                {config.purpose}
              </Typography.Paragraph>
              <Typography.Text type="secondary">{lastChanged(rates)}</Typography.Text>
            </div>
            {editing ? (
              <Space>
                <Button onClick={stopEditing} disabled={saving}>
                  Cancel
                </Button>
                <Button type="primary" onClick={() => save(false)} loading={saving}>
                  Save
                </Button>
              </Space>
            ) : (
              <Button type="primary" onClick={startEditing} disabled={!rates}>
                Edit rates
              </Button>
            )}
          </Space>

          <div style={{ marginTop: 16 }}>
            <RatesTable
              table={table}
              rates={rates}
              editing={editing}
              edits={edits}
              errors={errors}
              onEdit={onEdit}
            />
          </div>

          <h4 style={{ marginTop: 32 }}>History</h4>
          <RatesHistory
            table={table}
            history={history}
            meta={meta}
            currentId={rates?.id}
            onPage={getHistory}
            onRestore={restore}
            restoring={restoring}
          />
        </div>
      </div>

      <ConfirmChanges
        items={confirming}
        labels={fieldLabels(rates)}
        saving={saving}
        onConfirm={() => {
          setConfirming([])
          save(true)
        }}
        onCancel={() => setConfirming([])}
      />
    </PageLoader>
  )
}

RatesPage.propTypes = {
  table: PropTypes.oneOf(['banking', 'car']).isRequired
}

export default RatesPage
